import type { ComparisonRow, DeploymentModel, DeploymentNode, DeploymentOption, InformationItem, ModelId } from './types';

export const deploymentOptions: DeploymentOption[] = [
  {
    "id": "cloud",
    "label": "Cloud",
    "description": "Somatiq hosts everything. You add one small gateway PC.",
    "accent": "#1F4FE0",
    "tint": "#E6ECFD"
  },
  {
    "id": "hybrid",
    "label": "Hybrid",
    "description": "A local server for fast reading, with the cloud as backup and archive.",
    "accent": "#0B8577",
    "tint": "#E0F3F0"
  },
  {
    "id": "onprem",
    "label": "On-premises",
    "description": "Everything runs in your data centre. Data never leaves your network.",
    "accent": "#C96A12",
    "tint": "#FCEEDF"
  }
];

export const deploymentNodes: Record<string, DeploymentNode> = {
  "modalities": {
    "title": "Imaging modalities",
    "subtitle": "CT, MRI, X-ray, USG, PET-CT",
    "location": "Your hospital",
    "description": "Your scanners send studies over standard DICOM. Each one is simply pointed to a new DICOM destination."
  },
  "his": {
    "title": "HIS / EMR / LIS",
    "subtitle": "Orders and reports",
    "location": "Your hospital",
    "description": "Orders flow in and signed reports flow back over HL7, so staff keep working in the systems they already use."
  },
  "ws": {
    "title": "Workstations",
    "subtitle": "Radiologists, web viewer",
    "location": "Your hospital",
    "description": "Radiologists open studies in the Somatiq viewer from any workstation on your network."
  },
  "vna": {
    "title": "Your existing archive",
    "subtitle": "GE, Mediff or any VNA",
    "location": "Your hospital",
    "description": "Studies stay exactly where they are today. Somatiq PACS creates index entries that point to this storage, so nothing is copied or moved and the link happens automatically."
  },
  "pusher": {
    "title": "Local pusher",
    "subtitle": "Gateway PC",
    "location": "Your hospital · you provide",
    "description": "A small gateway PC that receives studies from your scanners and sends them to the Somatiq cloud over an encrypted connection.",
    "specs": [
      {
        "label": "Operating system",
        "value": "Windows 10 or later"
      },
      {
        "label": "Processor",
        "value": "Intel i5"
      },
      {
        "label": "Memory",
        "value": "8 GB or more"
      },
      {
        "label": "Local retention",
        "value": "3 days, configurable"
      }
    ]
  },
  "cpacs": {
    "title": "Somatiq PACS",
    "subtitle": "Cloud",
    "location": "Somatiq cloud",
    "description": "Stores and serves every study.",
    "managed": true
  },
  "app": {
    "title": "Somatiq application",
    "subtitle": "RIS, viewer, AI reporting",
    "location": "Somatiq cloud",
    "description": "Worklists, scheduling, the diagnostic viewer and AI-assisted reporting.",
    "managed": true
  },
  "blob": {
    "title": "Oracle Cloud storage",
    "subtitle": "Encrypted image archive",
    "location": "Somatiq cloud · Oracle Cloud Infrastructure",
    "description": "All image data is stored in Oracle Cloud Infrastructure (OCI) Object Storage. Data is encrypted in transit over TLS and at rest with AES-256, which OCI applies by default and cannot be switched off. Each object has its own encryption key.",
    "managed": true
  },
  "db": {
    "title": "Database",
    "subtitle": "Patients, orders, reports",
    "location": "Somatiq cloud",
    "description": "Patient, order and report records.",
    "managed": true
  },
  "remote": {
    "title": "Remote radiologists",
    "subtitle": "Teleradiology",
    "location": "Anywhere",
    "description": "Radiologists and teleradiology partners report from anywhere through a secure browser connection."
  },
  "lpacs": {
    "title": "Somatiq PACS",
    "subtitle": "Local server",
    "location": "Your hospital · you provide the server",
    "description": "Keeps recent studies on-site for fast reading and syncs everything to the Somatiq cloud for backup and long-term archive.",
    "specs": [
      {
        "label": "Operating system",
        "value": "Ubuntu 24 or later"
      },
      {
        "label": "Processor",
        "value": "4 vCPU"
      },
      {
        "label": "Memory",
        "value": "16 GB"
      },
      {
        "label": "Storage",
        "value": "1 TB, sized to your study volume"
      },
      {
        "label": "When local storage is full",
        "value": "Oldest studies are cleared locally; the cloud copy remains"
      }
    ]
  },
  "hstore": {
    "title": "Extra storage",
    "subtitle": "NAS, multiple volumes",
    "location": "Your hospital · optional",
    "description": "Add NAS units to grow local capacity. Multiple storage volumes can be attached as your volume increases."
  },
  "oserver": {
    "title": "Somatiq server",
    "subtitle": "PACS, RIS, viewer, AI",
    "location": "Your data centre · you provide the server",
    "description": "One machine runs Somatiq PACS and the full application: worklists, scheduling, the diagnostic viewer and AI-assisted reporting. For high study volumes, the same machine is simply sized up.",
    "tiers": [
      {
        "title": "Standard load",
        "specs": [
          {
            "label": "Processor",
            "value": "8 vCPU"
          },
          {
            "label": "Memory",
            "value": "32 GB"
          },
          {
            "label": "Disk",
            "value": "1 TB SSD"
          }
        ]
      },
      {
        "title": "High load",
        "specs": [
          {
            "label": "Processor",
            "value": "16 vCPU"
          },
          {
            "label": "Memory",
            "value": "64 GB"
          },
          {
            "label": "Disk",
            "value": "2 TB SSD"
          }
        ]
      }
    ]
  },
  "oremote": {
    "title": "Remote radiologists",
    "subtitle": "Optional",
    "location": "Outside your network",
    "description": "Radiologists can also report from outside the hospital, if your IT team allows secure external access to the Somatiq server."
  },
  "ostore": {
    "title": "Image storage",
    "subtitle": "NAS / SAN, multi-volume",
    "location": "Your data centre",
    "description": "Use existing or new NAS / SAN. Attach multiple storage volumes and extend capacity as volume grows."
  }
};

export const deploymentModels: Record<ModelId, DeploymentModel> = {
  "cloud": {
    "summary": "Somatiq runs the PACS and application. Your site needs only a gateway PC.",
    "zones": [
      {
        "label": "Your hospital",
        "x": 20,
        "y": 20,
        "w": 430,
        "h": 520,
        "client": true
      },
      {
        "label": "Somatiq cloud · managed by Somatiq",
        "x": 540,
        "y": 20,
        "w": 440,
        "h": 370
      }
    ],
    "nodes": {
      "modalities": [
        45,
        70
      ],
      "his": [
        45,
        230
      ],
      "ws": [
        45,
        400
      ],
      "pusher": [
        255,
        70
      ],
      "vna": [
        255,
        400
      ],
      "cpacs": [
        570,
        90
      ],
      "blob": [
        785,
        90
      ],
      "app": [
        570,
        260
      ],
      "db": [
        785,
        260
      ],
      "remote": [
        680,
        450
      ]
    },
    "edges": [
      {
        "source": "modalities",
        "target": "pusher",
        "label": "DICOM"
      },
      {
        "source": "pusher",
        "target": "cpacs",
        "label": "Encrypted"
      },
      {
        "source": "cpacs",
        "target": "blob",
        "label": ""
      },
      {
        "source": "cpacs",
        "target": "app",
        "label": ""
      },
      {
        "source": "app",
        "target": "db",
        "label": ""
      },
      {
        "source": "his",
        "target": "app",
        "label": "HL7"
      },
      {
        "source": "app",
        "target": "ws",
        "label": "Web viewer"
      },
      {
        "source": "app",
        "target": "remote",
        "label": "Secure web"
      },
      {
        "source": "vna",
        "target": "cpacs",
        "label": "Linked, not moved",
        "kind": "mig"
      }
    ],
    "lede": "For the cloud model, your IT team provides one gateway PC and network access. We handle everything else.",
    "steps": [
      {
        "title": "Site assessment",
        "description": "We review your modalities, HIS and network with your IT team and agree the go-live plan.",
        "providedByHospital": false
      },
      {
        "title": "Set up the gateway PC",
        "description": "You provide a PC to the pusher specification. We install and configure the Somatiq pusher on it.",
        "providedByHospital": true
      },
      {
        "title": "Connect scanners and HIS",
        "description": "Each modality is pointed to Somatiq as a DICOM destination, and the HL7 interface is set up for orders and reports.",
        "providedByHospital": false
      },
      {
        "title": "Link your existing archive",
        "description": "Studies in your VNA are indexed in place. Nothing is copied or moved.",
        "providedByHospital": false
      },
      {
        "title": "Train and go live",
        "description": "We train radiologists, technicians and front-desk staff, then switch reading over to Somatiq.",
        "providedByHospital": false
      },
      {
        "title": "Ongoing support",
        "description": "Our engineering team monitors the platform and supports your staff after go-live.",
        "providedByHospital": false
      }
    ]
  },
  "hybrid": {
    "summary": "A local Somatiq PACS server gives fast reading on-site; the cloud keeps the archive and enables remote reporting.",
    "zones": [
      {
        "label": "Your hospital",
        "x": 20,
        "y": 20,
        "w": 450,
        "h": 520,
        "client": true
      },
      {
        "label": "Somatiq cloud · managed by Somatiq",
        "x": 560,
        "y": 20,
        "w": 420,
        "h": 370
      }
    ],
    "nodes": {
      "modalities": [
        45,
        70
      ],
      "his": [
        45,
        230
      ],
      "ws": [
        45,
        400
      ],
      "lpacs": [
        260,
        230
      ],
      "hstore": [
        260,
        70
      ],
      "vna": [
        260,
        400
      ],
      "cpacs": [
        590,
        90
      ],
      "blob": [
        790,
        90
      ],
      "app": [
        590,
        260
      ],
      "remote": [
        700,
        450
      ]
    },
    "edges": [
      {
        "source": "modalities",
        "target": "lpacs",
        "label": "DICOM"
      },
      {
        "source": "lpacs",
        "target": "hstore",
        "label": ""
      },
      {
        "source": "lpacs",
        "target": "ws",
        "label": "Local reading"
      },
      {
        "source": "lpacs",
        "target": "cpacs",
        "label": "Encrypted sync"
      },
      {
        "source": "cpacs",
        "target": "blob",
        "label": ""
      },
      {
        "source": "cpacs",
        "target": "app",
        "label": ""
      },
      {
        "source": "his",
        "target": "app",
        "label": "HL7"
      },
      {
        "source": "app",
        "target": "remote",
        "label": "Secure web"
      },
      {
        "source": "vna",
        "target": "lpacs",
        "label": "Linked, not moved",
        "kind": "mig"
      }
    ],
    "lede": "For the hybrid model, your IT team provides a local server (and NAS if you want more capacity). The cloud side is managed by Somatiq.",
    "steps": [
      {
        "title": "Site assessment",
        "description": "We review your modalities, HIS, network and expected study volume, and size the local server.",
        "providedByHospital": false
      },
      {
        "title": "Provision the local server",
        "description": "You provide a server to the local PACS specification, plus NAS or additional volumes if needed.",
        "providedByHospital": true
      },
      {
        "title": "Install Somatiq PACS on-site",
        "description": "We install and configure the local Somatiq PACS and connect it securely to the Somatiq cloud.",
        "providedByHospital": false
      },
      {
        "title": "Connect scanners and HIS",
        "description": "Modalities send to the local server over DICOM, and the HL7 interface is set up for orders and reports.",
        "providedByHospital": false
      },
      {
        "title": "Link your existing archive",
        "description": "Studies in your VNA are indexed in place. Nothing is copied or moved.",
        "providedByHospital": false
      },
      {
        "title": "Train and go live",
        "description": "We train your team and switch reading over to Somatiq.",
        "providedByHospital": false
      },
      {
        "title": "Ongoing support",
        "description": "Our engineering team monitors both the local server and the cloud, and supports your staff.",
        "providedByHospital": false
      }
    ]
  },
  "onprem": {
    "summary": "Somatiq PACS and the application run on one server in your data centre. Patient data stays inside your network.",
    "zones": [
      {
        "label": "Your hospital network",
        "x": 20,
        "y": 20,
        "w": 430,
        "h": 520,
        "client": true
      },
      {
        "label": "Your data centre",
        "x": 520,
        "y": 20,
        "w": 460,
        "h": 390,
        "client": true,
        "dc": true
      }
    ],
    "nodes": {
      "modalities": [
        45,
        70
      ],
      "his": [
        45,
        230
      ],
      "ws": [
        45,
        360
      ],
      "vna": [
        255,
        460
      ],
      "oserver": [
        560,
        200
      ],
      "ostore": [
        785,
        200
      ],
      "oremote": [
        680,
        460
      ]
    },
    "edges": [
      {
        "source": "modalities",
        "target": "oserver",
        "label": "DICOM"
      },
      {
        "source": "oserver",
        "target": "ostore",
        "label": ""
      },
      {
        "source": "his",
        "target": "oserver",
        "label": "HL7"
      },
      {
        "source": "oserver",
        "target": "ws",
        "label": "Local network"
      },
      {
        "source": "oserver",
        "target": "oremote",
        "label": "If your IT allows",
        "kind": "opt"
      },
      {
        "source": "vna",
        "target": "oserver",
        "label": "Linked, not moved",
        "kind": "mig"
      }
    ],
    "lede": "For the on-premises model, your IT team provides one server and storage. We install, configure and support the software.",
    "steps": [
      {
        "title": "Site assessment",
        "description": "We review your modalities, HIS, data centre and expected study volume with your IT team.",
        "providedByHospital": false
      },
      {
        "title": "Provision the server and storage",
        "description": "You provide one server to specification (the larger size if your study volume is high), plus NAS / SAN storage.",
        "providedByHospital": true
      },
      {
        "title": "Install Somatiq PACS and application",
        "description": "We install and configure the full Somatiq stack on that one server.",
        "providedByHospital": false
      },
      {
        "title": "Connect scanners and HIS",
        "description": "Modalities send to the Somatiq server over DICOM, and the HL7 interface is set up, all within your network. If your IT team allows it, we also enable secure remote reporting.",
        "providedByHospital": false
      },
      {
        "title": "Link your existing archive",
        "description": "Studies in your VNA are indexed in place. Nothing is copied or moved.",
        "providedByHospital": false
      },
      {
        "title": "Train and go live",
        "description": "We train your team and switch reading over to Somatiq.",
        "providedByHospital": false
      },
      {
        "title": "Ongoing support",
        "description": "Our engineering team supports your IT staff after go-live.",
        "providedByHospital": false
      }
    ]
  }
};

export const comparisonRows: ComparisonRow[] = [
  {
    "label": "Where images are stored",
    "values": {
      "cloud": "Oracle Cloud, managed by Somatiq",
      "hybrid": "Your local server, with a copy on Oracle Cloud",
      "onprem": "Your data centre only"
    }
  },
  {
    "label": "Encryption",
    "values": {
      "cloud": "TLS in transit, AES-256 at rest",
      "hybrid": "TLS in transit, AES-256 at rest for the cloud copy",
      "onprem": "Data stays on your own storage"
    }
  },
  {
    "label": "What your IT team provides",
    "values": {
      "cloud": "One gateway PC",
      "hybrid": "A local server, plus NAS if needed",
      "onprem": "One server and NAS / SAN"
    }
  },
  {
    "label": "Reading on-site",
    "values": {
      "cloud": "Through your internet connection",
      "hybrid": "Local network speed",
      "onprem": "Local network speed"
    }
  },
  {
    "label": "Remote reporting",
    "values": {
      "cloud": "Built in",
      "hybrid": "Built in",
      "onprem": "Available if your IT allows external access"
    }
  },
  {
    "label": "Adding storage",
    "values": {
      "cloud": "Handled by Somatiq",
      "hybrid": "Add NAS or storage volumes",
      "onprem": "Add NAS / SAN or storage volumes"
    }
  },
  {
    "label": "Existing VNA data",
    "values": {
      "cloud": "Linked in place",
      "hybrid": "Linked in place",
      "onprem": "Linked in place"
    }
  }
];

export const securityItems: InformationItem[] = [
  {
    "status": "Certified",
    "title": "ISO/IEC 27001:2022",
    "description": "Our information security management system is certified to ISO/IEC 27001:2022 through an accredited audit.",
    "inProgress": false
  },
  {
    "status": "Compliant",
    "title": "HIPAA",
    "description": "Patient data handling follows HIPAA safeguards for privacy, access and security. We manage HIPAA compliance on Sprinto, so our controls are monitored continuously and compliance stays a priority every day, not just at audit time.",
    "inProgress": false
  },
  {
    "status": "In progress",
    "title": "CDSCO registration",
    "description": "Registration with CDSCO for the Somatiq platform is in progress. We will share documentation as it is filed.",
    "inProgress": true
  },
  {
    "status": "Always on",
    "title": "Encrypted in transit",
    "description": "Studies and reports travel between your site and the Somatiq cloud over TLS.",
    "inProgress": false
  },
  {
    "status": "Always on",
    "title": "Encrypted at rest",
    "description": "Image data on Oracle Cloud Infrastructure is encrypted with AES-256, with a separate key for every object.",
    "inProgress": false
  },
  {
    "status": "Ongoing",
    "title": "Continuous compliance monitoring",
    "description": "Our ISO 27001 and HIPAA controls are tracked continuously on Sprinto, a compliance automation platform, so gaps are caught as they appear.",
    "inProgress": false
  },
  {
    "status": "Built in",
    "title": "Audit trails",
    "description": "User activity is logged, reports keep a full version history, and records lock automatically to prevent conflicting edits.",
    "inProgress": false
  }
];

export const archiveItems: InformationItem[] = [
  {
    "title": "No data is moved",
    "description": "Studies already in your VNA stay where they are. Somatiq PACS creates index entries that point to the existing storage, and the link runs automatically."
  },
  {
    "title": "Storage that grows with you",
    "description": "On your premises, add NAS units or attach multiple storage volumes whenever volume increases."
  }
];
